import * as React from 'react'
import { CookieBanner } from '@lacedltd/laced-components'
import {
  useCookiePopUpState,
  useUser,
  useCookieConsentDecision,
  useTranslation,
  useLocale,
  useQueryParams
} from 'hooks'
import { withTranslations } from 'helpers'

const CookiePreferencePopUp: React.FC = () => {
  const [open, setOpen] = useCookiePopUpState()
  const { t } = useTranslation()
  const { localiseUrl } = useLocale()
  const { mobileAppRedirect } = useQueryParams(['mobileAppRedirect'])
  const { user } = useUser()
  const {
    existingCookiePreferences,
    error,
    updateCookieConsentDecision,
    loading
  } = useCookieConsentDecision()

  React.useEffect(() => {
    if (loading || error) return

    if (mobileAppRedirect) {
      const saveEssentialOnlyCookies = async () => {
        await updateCookieConsentDecision(user?.id, {
          essential: true,
          analytical: false,
          functional: false,
          advertising: false
        })
      }

      saveEssentialOnlyCookies()
      setOpen(false)
      return
    }

    if (!existingCookiePreferences?.valid) {
      setOpen(true)
    }
  }, [
    existingCookiePreferences,
    loading,
    error,
    setOpen,
    mobileAppRedirect,
    updateCookieConsentDecision,
    user?.id
  ])

  const preferences = React.useMemo(() => {
    return [
      {
        id: 'essential',
        label: t('react_components.cookies.essential'),
        description: t('react_components.cookies.essential_desc'),
        active: true,
        alwaysActive: true
      },
      {
        id: 'analytical',
        label: t('react_components.cookies.analytical'),
        description: t('react_components.cookies.analytical_desc'),
        active: existingCookiePreferences?.preferences?.analytical || true
      },
      {
        id: 'functional',
        label: t('react_components.cookies.functional'),
        description: t('react_components.cookies.functional_desc'),
        active: existingCookiePreferences?.preferences?.functional || true
      },
      {
        id: 'advertising',
        label: t('react_components.cookies.advertising'),
        description: t('react_components.cookies.advertising_desc'),
        active: existingCookiePreferences?.preferences?.advertising || true
      }
    ]
  }, [
    existingCookiePreferences?.preferences?.advertising,
    existingCookiePreferences?.preferences?.analytical,
    existingCookiePreferences?.preferences?.functional,
    t
  ])

  const onSavePreferences = async (preferences: Record<string, boolean>) => {
    try {
      const updatedPreferences = await updateCookieConsentDecision(
        user?.id,
        preferences as any
      )

      if (updatedPreferences && 'dataLayer' in window) {
        window.dataLayer?.push({
          event: 'cookiebox',
          preferences: updatedPreferences?.preferences
        })
      }
    } catch (error) {
      // Handle the error
      console.error(error)
    } finally {
      setOpen(false)
    }
  }

  return (
    <CookieBanner
      open={open}
      simpleFormCopy={{
        title: t('react_components.cookies.title'),
        disclaimer: t('react_components.cookies.cookies_disclaimer'),
        discoverMore: (
          <>
            {t('react_components.cookies.discover_more')}{' '}
            <a
              href={localiseUrl('/pages/cookies-policy')}
              className="underline medium"
            >
              {t('react_components.cookies.cookie_policy')}
            </a>
            .
          </>
        ),
        allowAllBtnText: t('react_components.cookies.allow_all'),
        expandPreferencesBtnText: t('react_components.cookies.preferences')
      }}
      expandedFormCopy={{
        title: t('react_components.cookies.cookies_breakdown'),
        savePreferencesBtnText: t('react_components.cookies.save_prefs'),
        checkboxActiveLabel: t('react_components.cookies.checkbox_active'),
        checkboxInactiveLabel: t('react_components.cookies.checkbox_inactive'),
        checkboxAlwaysActiveLabel: t('react_components.cookies.always_active')
      }}
      preferences={preferences}
      onSavePreferences={onSavePreferences}
    />
  )
}

export default withTranslations(CookiePreferencePopUp)
